import { useIntl } from 'react-intl';
import * as z from 'zod';

import { type ValidationError } from 'src/types';
import { generalValidationMessages } from 'src/utils/messages';

import type { FieldPath, FieldValues, UseFormSetError } from 'react-hook-form';

function mapErrors<TData extends FieldValues>(errors: ValidationError<TData>, setError: UseFormSetError<TData>) {
    const keys = Object.keys(errors) as Array<keyof TData>;
    keys.forEach((key) => {
        setError(key as FieldPath<TData>, {
            type: 'server',
            message: errors[key].join(', '),
        });
    });
}

const useZodCustomErrorMap = () => {
    const { formatMessage } = useIntl();
    const zodCallback: z.ZodErrorMap = (error, ctx) => {
        switch (error.code) {
            case z.ZodIssueCode.too_small:
                if (error.type === z.ZodParsedType.array) {
                    return { message: formatMessage(generalValidationMessages.fieldRequiredArray) };
                }
                if (error.type === z.ZodParsedType.string) {
                    return {
                        message: formatMessage(generalValidationMessages.fieldInvalidMinLength, {
                            min: error.minimum as number,
                        }),
                    };
                }
                return { message: formatMessage(generalValidationMessages.fieldRequired) };
            case z.ZodIssueCode.too_big:
                if (error.type === z.ZodParsedType.string) {
                    return {
                        message: formatMessage(generalValidationMessages.fieldInvalidMaxLength, {
                            max: error.maximum as number,
                        }),
                    };
                }
                if (error.type === z.ZodParsedType.number) {
                    return {
                        message: formatMessage(generalValidationMessages.fieldInvalidMaxLength, {
                            max: error.maximum as number,
                        }),
                    };
                }
                return { message: ctx.defaultError };
            case z.ZodIssueCode.invalid_date:
                return { message: formatMessage(generalValidationMessages.fieldRequiredDate) };
            case z.ZodIssueCode.invalid_string:
                if (error.validation === 'time') {
                    return { message: formatMessage(generalValidationMessages.fieldInvalidTime) };
                } else if (error.validation === 'email') {
                    return { message: formatMessage(generalValidationMessages.fieldInvalidEmail) };
                } else return { message: formatMessage(generalValidationMessages.fieldRequired) };
            case z.ZodIssueCode.invalid_type:
                return { message: formatMessage(generalValidationMessages.fieldRequiredSelect) };
            default:
                return { message: ctx.defaultError };
        }
    };

    return { zodCallback };
};

export { mapErrors, useZodCustomErrorMap };
